import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Stack,
  useTheme,
  alpha,
} from "@mui/material";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TimerOffIcon from "@mui/icons-material/TimerOff";
import SubtitlesOffIcon from "@mui/icons-material/SubtitlesOff";
import { ErrorData } from "../types";
import { InfoTooltip } from "./InfoTooltip";

interface ErrorPanelProps {
  data: ErrorData;
}

const METRICS = [
  {
    key: "connectionFailures" as const,
    label: "Connection Failures",
    description: "WebSocket connections that failed to establish. May indicate network issues or authentication problems.",
    icon: <LinkOffIcon sx={{ fontSize: 20 }} />,
    color: "error" as const,
  },
  {
    key: "operationErrors" as const,
    label: "Operation Errors",
    description: "Errors during WebSocket operations (save, read, regenerate, etc.). Includes lock conflicts and internal errors.",
    icon: <ErrorOutlineIcon sx={{ fontSize: 20 }} />,
    color: "error" as const,
  },
  {
    key: "sessionTimeouts" as const,
    label: "Session Timeouts",
    description: "Editing sessions that expired due to inactivity. Users are disconnected after the idle timeout period.",
    icon: <TimerOffIcon sx={{ fontSize: 20 }} />,
    color: "warning" as const,
  },
  {
    key: "transcriptionFailures" as const,
    label: "Transcription Failures (Global)",
    description: "Video transcription jobs that failed, preventing report generation from completing for those videos. This count is global across all stations and is not affected by the station filter.",
    icon: <SubtitlesOffIcon sx={{ fontSize: 20 }} />,
    color: "error" as const,
  },
];

export const ErrorPanel: React.FC<ErrorPanelProps> = ({ data }) => {
  const theme = useTheme();
  const totalErrors =
    data.connectionFailures +
    data.operationErrors +
    data.sessionTimeouts +
    data.transcriptionFailures;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        height: "100%",
        transition: "all 0.2s ease-in-out",
        "&:hover": { boxShadow: 2 },
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Errors
            </Typography>
            <InfoTooltip title="Errors and failures across the system in the selected time period. High counts may indicate infrastructure or configuration issues." />
          </Box>
        }
        subheader={`${totalErrors} total issue${totalErrors !== 1 ? "s" : ""} in period`}
      />
      <CardContent>
        <Stack spacing={2.5}>
          {METRICS.map((m) => {
            const value = data[m.key];
            return (
              <Box
                key={m.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: value > 0
                    ? alpha(theme.palette[m.color].main, 0.04)
                    : "transparent",
                  transition: "background-color 0.15s",
                  "&:hover": {
                    bgcolor: alpha(theme.palette[m.color].main, 0.08),
                  },
                }}
              >
                <Box
                  sx={{
                    color: value > 0 ? `${m.color}.main` : "text.disabled",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {m.icon}
                </Box>
                <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    color={value > 0 ? "text.secondary" : "text.disabled"}
                  >
                    {m.label}
                  </Typography>
                  <InfoTooltip title={m.description} />
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontVariantNumeric: "tabular-nums",
                    fontWeight: 700,
                  }}
                  color={value > 0 ? `${m.color}.main` : "text.disabled"}
                >
                  {value}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
};
